import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Table, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import {
  QUERY_ROLE_BY_PACKAGE_PAGE,
  type QueryRoleByPackagePageResponse,
  type QueryRoleByPackagePageVariables,
  type RoleByPackageType,
} from '../graphql/queries/queryRoleByPackagePage';

export interface PackageRolesDrawerProps {
  visible: boolean;
  packageId: number | string;
  packageName: string;
  onClose: () => void;
}

const PackageRolesDrawer: React.FC<PackageRolesDrawerProps> = ({ visible, packageId, packageName, onClose }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Drawer 组件本身不会随 open/close 卸载（父组件始终渲染，只切换 visible），
  // 分页状态需要在每次打开时手动重置为初始值，否则会带着上一次查看的权限包的状态。
  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [visible]);

  const variables: QueryRoleByPackagePageVariables = useMemo(
    () => ({
      packageId: Number(packageId),
      pageRequest: {
        page: currentPage,
        size: pageSize,
        sortDirection: 'DESC',
      },
    }),
    [packageId, currentPage, pageSize]
  );

  const { data, loading } = useQuery<QueryRoleByPackagePageResponse, QueryRoleByPackagePageVariables>(
    QUERY_ROLE_BY_PACKAGE_PAGE,
    {
      variables,
      skip: !visible || !packageId,
      fetchPolicy: 'network-only',
    }
  );

  const pageData = data?.queryRoleByPackagePage;

  const columns: TableColumnsType<RoleByPackageType> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
      },
      {
        title: t('central.platformPermission.role.table.name'),
        dataIndex: 'name',
        key: 'name',
        width: 160,
        render: (v) => v || '-',
      },
      {
        title: t('central.platformPermission.role.table.description'),
        dataIndex: 'description',
        key: 'description',
        width: 200,
        ellipsis: { showTitle: false },
        render: (v: string | null) => (
          <Typography.Text style={{ display: 'block' }} ellipsis={{ tooltip: v ?? '-' }}>
            {v ?? '-'}
          </Typography.Text>
        ),
      },
    ],
    [t]
  );

  return (
    <Drawer
      title={t('central.platformPermission.permissionPackage.rolesDrawer.title', { name: packageName })}
      open={visible}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <Table<RoleByPackageType>
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={columns}
        dataSource={pageData?.content ?? []}
        pagination={false}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageData?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
        onShowSizeChange={(_, size) => {
          setCurrentPage(1);
          setPageSize(size);
        }}
        pageSizeOptions={['10', '20', '50', '100']}
      />
    </Drawer>
  );
};

export default PackageRolesDrawer;
